import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const abortControllerCode = `// Precise Cancellation: AbortController
async function fetchWithTimeout(resource, options = {}) {
    const { timeout = 8000 } = options;
    
    // The control unit for cancellation
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(resource, {
            ...options,
            signal: controller.signal // Mapping the signal to the request
        });
        clearTimeout(id);
        return response;
    } catch (error) {
        if (error.name === 'AbortError') {
            console.error("Critical: Request reached timeout threshold.");
        }
        throw error;
    }
}`;

const interceptorCode = `// Architectural Logic: Request Interceptors
// While fetch doesn't have native interceptors, we engineer them via wrappers.
const apiWrapper = async (url, config = {}) => {
    // 1. Request Intercept: Injecting dynamic tokens
    config.headers = {
        ...config.headers,
        Authorization: \`Bearer \${localStorage.getItem('token')}\`,
        'X-Request-ID': crypto.randomUUID()
    };

    const response = await fetch(url, config);

    // 2. Response Intercept: Global error handling
    if (response.status === 401) {
        // Trigger global logout or token refresh
        window.location.href = '/login';
    }

    return response;
};`;

const streamingCode = `// High-Performance I/O: Streaming Responses
async function processLargeStream(url) {
    const response = await fetch(url);
    const reader = response.body.getReader();
    let bytesReceived = 0;

    // Reading the stream in chunks to minimize memory pressure
    while(true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        bytesReceived += value.length;
        console.log(\`Received \${bytesReceived} bytes of binary data...\`);
        // Process chunk (value) here without loading the full file into RAM
    }
}`;

const JSFetchAdvanced = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Advanced Networking</div>
                <h1>Advanced Fetch: Signals, Streams & Interceptors</h1>
                <p className="hero-lead">
                    Go beyond simple GET/POST. Master the high-level architectural patterns of the browser's
                    networking stack, including request cancellation, streaming I/O, and custom interceptor logic.
                </p>
            </div>

            <div className="content-section">
                <h2>The Signal Pattern: AbortController</h2>
                <p>
                    In production environments, a dangling request is a resource leak. Whether a user navigates
                    away from a page or a network operation takes too long, you must have a way to **abort** the
                    process. The `AbortController` serves as the signaling mechanism that allows you to
                    manually terminate an asynchronous `fetch` operation.
                </p>
                <p>
                    This is particularly critical for "Search-as-you-type" interfaces, where you want to
                    cancel the previous keystroke's request as the new one starts, preventing old
                    responses from overwriting fresh data (the classic "Race Condition").
                </p>
                <CodeBlock code={abortControllerCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Engineering Interceptor Logic</h2>
                <p>
                    Unlike libraries like Axios, the native `fetch` API doesn't provide a built-in
                    interceptor system. However, software engineers solve this by creating **API Wrappers**.
                    A wrapper allows you to centralize logic such as injecting Authentication tokens,
                    logging telemetry, or handling global error states (like HTTP 401 Unauthorized)
                    without repeating code in every component.
                </p>
                <ProTip><p>
                        Always use your wrapper to inject a unique `X-Request-ID` into every outgoing header.
                        This allows your backend engineers to trace a specific UI action all the way through
                        your distributed microservices logs.
                    </p></ProTip>
                <CodeBlock code={interceptorCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>High-Performance I/O: The Streams API</h2>
                <p>
                    When fetching massive assets (like 500MB CSVs or high-res video), calling `response.json()`
                    or `response.blob()` is dangerous because it loads the entire file into the browser's
                    memory (RAM). The **Streams API** allows you to process data **chunk-by-chunk** as it
                    arrives from the network.
                </p>
                <p>
                    By using a stream reader, you can update progress bars with byte-level accuracy and
                    perform calculations on data before the full transmission is even complete.
                </p>
                <CodeBlock code={streamingCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Cancellation:** Implement `AbortController` for all non-essential long-polls.</li>
                    <li>**Stability:** Use `finally` blocks to clear timeout IDs and cleanup state.</li>
                    <li>**Abstraction:** Never call `fetch` directly in components; use a shared API wrapper.</li>
                    <li>**Memory:** Use the Streams API (`getReader()`) for files larger than 10MB.</li>
                    <li>**Security:** Sanitize dynamic URL inputs to prevent SSRF vulnerabilities.</li>
                    <li>**Performance:** Leverage `AbortSignal.timeout()` for clean, browser-native timeouts.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSFetchAdvanced;


